/**
 * SendBundleModal (Iter 36.h) — email the Steering-Committee Bundle PDF
 * to a teammate or an external stakeholder.
 *
 * Mirrors the iter 36.c SendBriefModal UX (member quick-pick + free-form
 * email + optional personal note), but the backend attaches the freshly-
 * composed PDF rather than rendering the data inline into the email body.
 *
 * UX deltas from SendBriefModal:
 *   - Help text mentions the PDF attachment + the email body's 3-card
 *     executive read-out so recipients know what to expect before opening.
 *   - No "nothing to send" guard on the API side — the bundle is also
 *     valid on an empty workspace (the PDF renders an "import something
 *     first" note in that case). The submit button stays active.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  XIcon as X,
  EnvelopeSimpleIcon as Envelope,
  CircleNotchIcon as Spinner,
  UserIcon as User,
  FilePdfIcon as FilePdf,
} from "@phosphor-icons/react";
import {
  listWorkspaceMembers,
  sendSteeringBundleEmail,
} from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

const MAX_MESSAGE = 1000;
// Lightweight email shape check — backend re-validates with EmailStr.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SendBundleModal({
  workspaceId,
  workspaceName,
  clientName,
  open,
  onClose,
  onSent,
}) {
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedUserId("");
    setEmailInput("");
    setMessage("");
    setSubmitting(false);
    setLoadingMembers(true);
    listWorkspaceMembers(workspaceId)
      .then((res) => {
        setMembers((res.members || []).filter((m) => m.user_id));
      })
      .catch(() => setMembers([]))
      .finally(() => setLoadingMembers(false));
  }, [open, workspaceId]);

  const trimmedEmail = emailInput.trim().toLowerCase();
  const emailLooksValid = useMemo(
    () => !trimmedEmail || EMAIL_RE.test(trimmedEmail),
    [trimmedEmail],
  );

  const canSend =
    !submitting &&
    (selectedUserId || (trimmedEmail && emailLooksValid));

  const handleSend = async () => {
    if (!canSend) return;
    setSubmitting(true);
    try {
      const res = await sendSteeringBundleEmail(workspaceId, {
        recipientUserId: selectedUserId || undefined,
        recipientEmail: !selectedUserId && trimmedEmail ? trimmedEmail : undefined,
        personalMessage: message.trim() || undefined,
      });
      const dest = res.recipient_email || "the recipient";
      const sizeKb = res.bytes ? `${Math.max(1, Math.round(res.bytes / 1024))} KB` : "the PDF";
      toast.success(
        res.stubbed
          ? `Bundle (${sizeKb}) queued to ${dest} (stub mode — check backend logs).`
          : `Bundle PDF (${sizeKb}) sent to ${dest}.`,
      );
      onSent?.(res);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to send the bundle.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const title = clientName || workspaceName;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="send-bundle-modal-backdrop"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white border border-[var(--ml-border)] max-w-lg w-full shadow-xl"
        data-testid="send-bundle-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--ml-border)]">
          <div className="flex items-center gap-2">
            <FilePdf size={16} weight="bold" className="text-[var(--ml-primary)]" />
            <h3 className="font-semibold text-lg">Email steering bundle</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="send-bundle-modal-close"
            className="ml-btn-icon text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm space-y-1">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Bundle</div>
            <div className="font-semibold" data-testid="send-bundle-modal-title">{title}</div>
            {clientName && clientName !== workspaceName && (
              <div className="text-xs text-[var(--ml-text-secondary)]">{workspaceName}</div>
            )}
          </div>

          <div>
            <label
              htmlFor="send-bundle-member"
              className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1 flex items-center gap-1"
            >
              <User size={11} weight="bold" /> Workspace member
            </label>
            <select
              id="send-bundle-member"
              value={selectedUserId}
              onChange={(e) => {
                setSelectedUserId(e.target.value);
                if (e.target.value) setEmailInput("");
              }}
              disabled={loadingMembers || submitting}
              className="ml-input w-full text-sm"
              data-testid="send-bundle-modal-member-select"
            >
              <option value="">
                {loadingMembers
                  ? "Loading members…"
                  : members.length === 0
                  ? "No teammates on this workspace yet"
                  : "— choose a teammate —"}
              </option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.name} · {m.email} ({m.role})
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <div className="absolute left-0 right-0 -top-2 flex items-center justify-center">
              <span className="bg-white px-2 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                or
              </span>
            </div>
            <div className="border-t border-[var(--ml-border)] pt-3">
              <label
                htmlFor="send-bundle-email"
                className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1 flex items-center gap-1"
              >
                <Envelope size={11} weight="bold" /> External email
              </label>
              <input
                id="send-bundle-email"
                type="email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  if (e.target.value) setSelectedUserId("");
                }}
                placeholder="client.sponsor@example.com"
                disabled={submitting}
                className="ml-input w-full text-sm"
                data-testid="send-bundle-modal-email-input"
              />
              {!emailLooksValid && (
                <div className="text-[11px] text-[#a2191f] mt-1" data-testid="send-bundle-modal-email-error">
                  That doesn&rsquo;t look like a valid email.
                </div>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="send-bundle-note"
              className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1"
            >
              Personal note <span className="opacity-50 ml-mono">(optional, max {MAX_MESSAGE} chars)</span>
            </label>
            <textarea
              id="send-bundle-note"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
              placeholder="Hi Sam — bundle for Thursday's steering. The radar section is the headline; the rest is supporting evidence."
              rows={4}
              disabled={submitting}
              className="ml-input w-full text-sm"
              data-testid="send-bundle-modal-note"
            />
            <div className="text-right text-[10px] ml-mono text-[var(--ml-text-secondary)] mt-0.5">
              {message.length} / {MAX_MESSAGE}
            </div>
          </div>

          <div className="text-xs text-[var(--ml-text-secondary)] ml-card p-3 bg-[var(--ml-surface)]/40">
            The PDF is composed fresh from this workspace and attached to the
            message. The email body shows a 3-card executive read-out (brief /
            churn / radar) so the recipient can decide whether to open the
            attachment now or after the meeting. Reply-To is set to your
            address.
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--ml-border)] -mx-5 px-5 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="ml-btn-secondary"
              data-testid="send-bundle-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              data-testid="send-bundle-modal-send"
              className="ml-btn-primary"
              title={
                selectedUserId
                  ? "Send to selected teammate"
                  : trimmedEmail
                  ? `Send to ${trimmedEmail}`
                  : "Choose a teammate or enter an email"
              }
            >
              {submitting ? (
                <><Spinner size={12} className="animate-spin" /> Sending…</>
              ) : (
                <><FilePdf size={12} weight="bold" /> Send bundle</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
